import { useEffect, useReducer, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { createCase } from "@/features/cases/api";
import type { ResolutionType } from "@/features/cases/types";
import { listNoticeDefinitions, recommendResolution } from "@/features/notices/api";
import type {
  AdvisorInput,
  AdvisorRecommendation,
  NoticeDefinitionOption,
} from "@/features/notices/types";
import {
  type AdvisorAnswers,
  type AdvisorPhase,
  BLOCK_MESSAGE,
  type BlockReason,
  BUSINESS_OPTIONS,
  type ClientType,
  INDIVIDUAL_OPTIONS,
  nextFromInit,
  nextFromPrep,
  optionsPhaseFor,
  prepGuard,
} from "@/features/notices/components/advisor/advisor-machine";
import { formatCents } from "@/lib/utils";

interface State {
  phase: AdvisorPhase;
  stack: AdvisorPhase[];
  answers: AdvisorAnswers;
  recommendation: AdvisorRecommendation | null;
  blockReason: Exclude<BlockReason, null> | null;
  noticeDef: NoticeDefinitionOption | null;
  loading: boolean;
}

type Action =
  | { type: "reset" }
  | { type: "answer"; patch: AdvisorAnswers }
  | { type: "goto"; phase: AdvisorPhase }
  | { type: "back" }
  | { type: "result"; recommendation: AdvisorRecommendation }
  | { type: "block"; reason: Exclude<BlockReason, null> }
  | { type: "noticeDef"; def: NoticeDefinitionOption }
  | { type: "loading"; loading: boolean };

function initialState(): State {
  return {
    phase: "init",
    stack: [],
    answers: {},
    recommendation: null,
    blockReason: null,
    noticeDef: null,
    loading: false,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "reset":
      return initialState();
    case "answer":
      return { ...state, answers: { ...state.answers, ...action.patch } };
    case "goto":
      return { ...state, phase: action.phase, stack: [...state.stack, state.phase] };
    case "back": {
      const stack = [...state.stack];
      const prev = stack.pop();
      return prev ? { ...state, phase: prev, stack } : state;
    }
    case "result":
      return {
        ...state,
        recommendation: action.recommendation,
        // A recommendation and a notice-track selection are mutually exclusive
        // results — clear any stale notice def so ResultPhase renders correctly.
        noticeDef: null,
        phase: "result",
        stack: [...state.stack, state.phase],
      };
    case "block":
      return {
        ...state,
        blockReason: action.reason,
        phase: "blocked",
        stack: [...state.stack, state.phase],
      };
    case "noticeDef":
      // Clear any prior recommendation so the notice-track UI isn't masked by a
      // stale recommendation card (e.g. after backing out and switching intent).
      return { ...state, noticeDef: action.def, recommendation: null };
    case "loading":
      return { ...state, loading: action.loading };
    default:
      return state;
  }
}

interface AdvisorWizardProps {
  clientId: string;
  clientType: ClientType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Launch the Log-Notice intake prefilled with a definition (notice track). */
  onLogNotice: (noticeDefinitionId: string) => void;
  /** Navigate to a newly opened case. */
  onOpened: (caseId: string) => void;
}

/**
 * The Notice Advisor wizard: a branching Q&A that recommends a resolution type
 * and (for collection alternatives) IA payment options, then opens a case.
 * Phase navigation is local (see advisor-machine); the recommendation + IA math
 * come from the server.
 */
export function AdvisorWizard({
  clientId,
  clientType,
  open,
  onOpenChange,
  onLogNotice,
  onOpened,
}: AdvisorWizardProps) {
  const defaultPractitioner = useAuthStore((s) => s.user?.default_practitioner ?? "");
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (open) dispatch({ type: "reset" });
  }, [open]);

  const { phase, answers } = state;

  async function runRecommend(extra: AdvisorAnswers) {
    const input = { ...answers, ...extra } as AdvisorInput;
    dispatch({ type: "answer", patch: extra });
    dispatch({ type: "loading", loading: true });
    try {
      const rec = await recommendResolution(input);
      dispatch({ type: "result", recommendation: rec });
    } catch {
      toast.error("Couldn't compute a recommendation.");
    } finally {
      dispatch({ type: "loading", loading: false });
    }
  }

  async function openCase() {
    const rec = state.recommendation;
    if (!rec?.resolutionType) return;
    setOpening(true);
    try {
      const created = await createCase({
        client: clientId,
        resolution_type: rec.resolutionType as ResolutionType,
        assigned_practitioner: defaultPractitioner || null,
        status: "ACTIVE",
        balance: "0",
        tax_years: "",
      });
      toast.success("Case opened");
      onOpenChange(false);
      onOpened(created.id);
    } catch {
      toast.error("Couldn't open a case.");
    } finally {
      setOpening(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Notice Advisor</DialogTitle>
          <DialogDescription>
            Answer a few questions to get a recommended resolution path.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[180px]">
          {phase === "init" && (
            <InitPhase
              clientType={clientType}
              onChoose={(intent) => {
                dispatch({ type: "answer", patch: { intent, client_type: clientType } });
                dispatch({ type: "goto", phase: nextFromInit(intent, clientType) });
              }}
            />
          )}

          {phase === "notice" && (
            <NoticePhase
              onSelect={(def) => {
                dispatch({ type: "noticeDef", def });
                dispatch({ type: "goto", phase: "result" });
              }}
            />
          )}

          {phase === "abate" && (
            <AbatePhase
              loading={state.loading}
              onContinue={(basis) =>
                runRecommend({ intent: "abate", abate_basis: basis })
              }
            />
          )}

          {phase === "prep" && (
            <PrepPhase
              onContinue={(a) => {
                dispatch({ type: "answer", patch: a });
                const merged = { ...answers, ...a };
                const reason = prepGuard(merged);
                if (reason) dispatch({ type: "block", reason });
                else dispatch({ type: "goto", phase: nextFromPrep(merged) });
              }}
            />
          )}

          {phase === "bus_prep" && (
            <BusPrepPhase
              onContinue={(a) => {
                dispatch({ type: "answer", patch: a });
                dispatch({ type: "goto", phase: "bus_tax_due" });
              }}
            />
          )}

          {(phase === "tax_due" || phase === "bus_tax_due") && (
            <TaxDuePhase
              business={phase === "bus_tax_due"}
              onContinue={(a) => {
                dispatch({ type: "answer", patch: a });
                dispatch({ type: "goto", phase: optionsPhaseFor(clientType) });
              }}
            />
          )}

          {(phase === "options" || phase === "options2") && (
            <OptionsPhase
              loading={state.loading}
              options={phase === "options" ? INDIVIDUAL_OPTIONS : BUSINESS_OPTIONS}
              onContinue={(choice) => runRecommend({ solution_choice: choice })}
            />
          )}

          {phase === "blocked" && state.blockReason && (
            <p className="text-destructive text-sm">{BLOCK_MESSAGE[state.blockReason]}</p>
          )}

          {phase === "result" && (
            <ResultPhase
              recommendation={state.recommendation}
              noticeDef={state.noticeDef}
              opening={opening}
              onLogNotice={() => {
                if (state.noticeDef) {
                  onOpenChange(false);
                  onLogNotice(state.noticeDef.id);
                }
              }}
              onOpenCase={openCase}
            />
          )}
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            disabled={state.stack.length === 0}
            onClick={() => dispatch({ type: "back" })}
          >
            Back
          </Button>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- phase components -------------------------------------------------------

function InitPhase({
  clientType,
  onChoose,
}: {
  clientType: ClientType;
  onChoose: (intent: AdvisorInput["intent"]) => void;
}) {
  const [value, setValue] = useState<AdvisorInput["intent"] | "">("");
  return (
    <div className="grid gap-4">
      <Label>What are you helping your client with?</Label>
      <RadioGroup value={value} onValueChange={(v) => setValue(v as AdvisorInput["intent"])}>
        <Option value="notice" label="Respond to a notice/letter from the IRS" />
        <Option value="abate" label="Request penalty abatement" />
        <Option
          value="alternative"
          label={`Client owes tax / needs a collection alternative (${
            clientType === "I" ? "individual" : "business"
          })`}
        />
      </RadioGroup>
      <Button disabled={!value} onClick={() => value && onChoose(value)}>
        Continue
      </Button>
    </div>
  );
}

function NoticePhase({ onSelect }: { onSelect: (def: NoticeDefinitionOption) => void }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<NoticeDefinitionOption[]>([]);

  useEffect(() => {
    let active = true;
    const handle = setTimeout(() => {
      listNoticeDefinitions(term)
        .then((list) => active && setResults(list.slice(0, 20)))
        .catch(() => active && setResults([]));
    }, 200);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [term]);

  return (
    <div className="grid gap-3">
      <Label>What type of notice did the client receive?</Label>
      <Input
        placeholder="Search by code or keyword (e.g. CP504, levy)…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
      />
      <ul className="border-border flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border p-1">
        {results.map((d) => (
          <li key={d.id}>
            <button
              type="button"
              onClick={() => onSelect(d)}
              className="hover:bg-muted w-full rounded-md px-2 py-2 text-left text-sm"
            >
              <span className="text-foreground font-medium">{d.code}</span>{" "}
              <span className="text-muted-foreground">— {d.title}</span>
            </button>
          </li>
        ))}
        {results.length === 0 && (
          <li className="text-muted-foreground px-2 py-2 text-sm">No matches.</li>
        )}
      </ul>
    </div>
  );
}

function AbatePhase({
  loading,
  onContinue,
}: {
  loading: boolean;
  onContinue: (basis: "first_time" | "reasonable_cause") => void;
}) {
  const [value, setValue] = useState<"first_time" | "reasonable_cause" | "">("");
  return (
    <div className="grid gap-4">
      <Label>Why does the client deserve abatement?</Label>
      <RadioGroup value={value} onValueChange={(v) => setValue(v as typeof value)}>
        <Option value="first_time" label="First-Time Abatement (clean compliance history)" />
        <Option value="reasonable_cause" label="Reasonable cause" />
      </RadioGroup>
      <Button disabled={!value || loading} onClick={() => value && onContinue(value)}>
        Continue
      </Button>
    </div>
  );
}

function YesNo({
  name,
  label,
  value,
  onChange,
}: {
  /** Unique key so the yes/no radio ids don't collide across questions. */
  name: string;
  label: string;
  value: boolean | undefined;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label>{label}</Label>
      <RadioGroup
        className="flex gap-6"
        value={value === undefined ? "" : value ? "yes" : "no"}
        onValueChange={(v) => onChange(v === "yes")}
      >
        {[
          { v: "yes", label: "Yes" },
          { v: "no", label: "No" },
        ].map(({ v, label: optLabel }) => {
          const id = `${name}-${v}`;
          return (
            <div key={v} className="flex items-center gap-2">
              <RadioGroupItem value={v} id={id} />
              <Label htmlFor={id} className="text-sm font-normal">
                {optLabel}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}

function PrepPhase({ onContinue }: { onContinue: (a: AdvisorAnswers) => void }) {
  const [a, setA] = useState<AdvisorAnswers>({});
  const complete =
    a.filed !== undefined &&
    a.bankrupt !== undefined &&
    a.payments !== undefined &&
    a.entered !== undefined;
  return (
    <div className="grid gap-4">
      <YesNo
        name="filed"
        label="Have all returns been filed with the IRS?"
        value={a.filed}
        onChange={(v) => setA((p) => ({ ...p, filed: v }))}
      />
      <YesNo
        name="bankrupt"
        label="Is the client currently in bankruptcy?"
        value={a.bankrupt}
        onChange={(v) => setA((p) => ({ ...p, bankrupt: v }))}
      />
      <YesNo
        name="payments"
        label="Are estimated payments current?"
        value={a.payments}
        onChange={(v) => setA((p) => ({ ...p, payments: v }))}
      />
      <YesNo
        name="entered"
        label="Have you entered all of the client's federal tax owed?"
        value={a.entered}
        onChange={(v) => setA((p) => ({ ...p, entered: v }))}
      />
      <Button disabled={!complete} onClick={() => onContinue(a)}>
        Continue
      </Button>
    </div>
  );
}

function BusPrepPhase({ onContinue }: { onContinue: (a: AdvisorAnswers) => void }) {
  const [a, setA] = useState<AdvisorAnswers>({});
  const complete =
    a.bus_active !== undefined &&
    a.bus_soleprop !== undefined &&
    a.bus_payroll !== undefined;
  return (
    <div className="grid gap-4">
      <YesNo
        name="bus_active"
        label="Is the business still active?"
        value={a.bus_active}
        onChange={(v) => setA((p) => ({ ...p, bus_active: v }))}
      />
      <YesNo
        name="bus_soleprop"
        label="Is the business a sole proprietorship?"
        value={a.bus_soleprop}
        onChange={(v) => setA((p) => ({ ...p, bus_soleprop: v }))}
      />
      <YesNo
        name="bus_payroll"
        label="Does the delinquent tax include payroll tax (Forms 940/941)?"
        value={a.bus_payroll}
        onChange={(v) => setA((p) => ({ ...p, bus_payroll: v }))}
      />
      <Button disabled={!complete} onClick={() => onContinue(a)}>
        Continue
      </Button>
    </div>
  );
}

const INDIVIDUAL_RANGES = [
  { value: 1, label: "$10,000 or less" },
  { value: 2, label: "$10,001 – $50,000" },
  { value: 3, label: "$50,001 – $250,000" },
  { value: 4, label: "More than $250,000" },
];
const BUSINESS_RANGES = [
  { value: 1, label: "$10,000 or less" },
  { value: 2, label: "$10,001 – $25,000" },
  { value: 3, label: "$25,001 – $50,000" },
  { value: 4, label: "More than $50,000" },
];

function TaxDuePhase({
  business,
  onContinue,
}: {
  business: boolean;
  onContinue: (a: AdvisorAnswers) => void;
}) {
  const [amount, setAmount] = useState("");
  const [range, setRange] = useState("");
  const ranges = business ? BUSINESS_RANGES : INDIVIDUAL_RANGES;
  // The backend requires a whole amount ≥ 1; guard here so Continue can't POST
  // a 0/negative/blank amount that just bounces back as a generic error.
  const amountNum = Number(amount);
  const amountValid = amount.trim() !== "" && Number.isFinite(amountNum) && amountNum >= 1;
  const valid = amountValid || range !== "";

  function submit() {
    if (amountValid) onContinue({ tax_due_amt: amount.trim() });
    else if (range !== "") onContinue({ tax_due_range: Number(range) });
  }

  return (
    <div className="grid gap-4">
      <Label>Estimate the total tax owed (excluding interest and penalties)</Label>
      <div className="grid gap-1.5">
        <Label className="text-muted-foreground text-xs">Exact amount</Label>
        <Input
          type="number"
          min="1"
          placeholder="e.g. 12500"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            if (e.target.value) setRange("");
          }}
        />
      </div>
      <div className="text-muted-foreground text-center text-xs">— or pick a range —</div>
      <RadioGroup
        value={range}
        onValueChange={(v) => {
          setRange(v);
          setAmount("");
        }}
      >
        {ranges.map((r) => (
          <Option key={r.value} value={String(r.value)} label={r.label} />
        ))}
      </RadioGroup>
      <Button disabled={!valid} onClick={submit}>
        Continue
      </Button>
    </div>
  );
}

function OptionsPhase({
  loading,
  options,
  onContinue,
}: {
  loading: boolean;
  options: { value: string; label: string }[];
  onContinue: (choice: string) => void;
}) {
  const [value, setValue] = useState("");
  return (
    <div className="grid gap-4">
      <Label>Here are the client's options:</Label>
      <RadioGroup value={value} onValueChange={setValue}>
        {options.map((o) => (
          <Option key={o.value} value={o.value} label={o.label} />
        ))}
      </RadioGroup>
      <Button disabled={!value || loading} onClick={() => value && onContinue(value)}>
        Continue
      </Button>
    </div>
  );
}

function ResultPhase({
  recommendation,
  noticeDef,
  opening,
  onLogNotice,
  onOpenCase,
}: {
  recommendation: AdvisorRecommendation | null;
  noticeDef: NoticeDefinitionOption | null;
  opening: boolean;
  onLogNotice: () => void;
  onOpenCase: () => void;
}) {
  // Notice track: show the selected definition + offer to log it.
  if (noticeDef && !recommendation) {
    return (
      <div className="grid gap-3">
        <h3 className="text-foreground font-semibold">
          {noticeDef.code} — {noticeDef.title}
        </h3>
        <p className="text-muted-foreground text-sm">{noticeDef.recommendedAction}</p>
        <Button onClick={onLogNotice}>Log this notice</Button>
      </div>
    );
  }

  if (!recommendation) return null;
  const rec = recommendation;

  return (
    <div className="grid gap-3">
      <h3 className="text-foreground font-semibold">Recommendation</h3>
      {rec.resolutionType && (
        <p className="text-foreground text-sm">
          Suggested resolution: <span className="font-semibold">{rec.resolutionType}</span>
        </p>
      )}
      {rec.warnings.map((w) => (
        <p key={w} className="text-warning text-sm">
          ⚠ {w}
        </p>
      ))}
      {rec.messages.map((m) => (
        <p key={m} className="text-muted-foreground text-sm">
          {m}
        </p>
      ))}
      {rec.iaPaymentOptions.length > 0 && (
        <ul className="border-border grid gap-1 rounded-md border p-3">
          {rec.iaPaymentOptions.map((o) => (
            <li key={o.label} className="text-foreground text-sm">
              {o.monthlyPaymentCents !== null && o.months !== null ? (
                <>
                  <span className="font-semibold">
                    {formatCents(o.monthlyPaymentCents)}/mo
                  </span>{" "}
                  for {o.months} months —{" "}
                </>
              ) : null}
              {o.label}
            </li>
          ))}
        </ul>
      )}
      {rec.terminalPhase === "online_ia" && (
        <a
          href="https://www.irs.gov/payments/online-payment-agreement-application"
          target="_blank"
          rel="noreferrer"
          className="text-primary text-sm font-medium hover:underline"
        >
          Set up an Online Payment Agreement at irs.gov →
        </a>
      )}
      {rec.opensCase && rec.resolutionType && (
        <Button onClick={onOpenCase} disabled={opening}>
          Open {rec.resolutionType} case
        </Button>
      )}
    </div>
  );
}

function Option({
  value,
  label,
  inline,
}: {
  value: string;
  label: string;
  inline?: boolean;
}) {
  const id = `opt-${value}`;
  return (
    <div className={inline ? "flex items-center gap-2" : "flex items-start gap-2"}>
      <RadioGroupItem value={value} id={id} />
      <Label htmlFor={id} className="text-sm font-normal leading-snug">
        {label}
      </Label>
    </div>
  );
}
